import { useCallback, useEffect, useRef, useState } from "react";

const CREATE_REALTIME_SESSION_URL =
  "https://us-west1-wui-go-97d87.cloudfunctions.net/create_realtime_session";
const OPENAI_REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

const STATUS = {
  IDLE: "idle",
  CONNECTING: "connecting",
  ACTIVE: "active",
  ERROR: "error"
};

const isMicPermissionDenied = (err) =>
  err?.name === "NotAllowedError" ||
  err?.name === "PermissionDeniedError" ||
  /permission/i.test(String(err?.message || ""));

const normalizeBackendError = async (res) => {
  let details = "";
  try {
    const json = await res.clone().json();
    details =
      json?.error?.message ||
      json?.message ||
      JSON.stringify(json);
  } catch (_) {
    try {
      details = await res.text();
    } catch (_) {
      details = "";
    }
  }

  const base = `Realtime session backend failed (${res.status})`;
  return details ? `${base}: ${details}` : base;
};

export default function useRealtimeNextStepsAssistant({
  isModalOpen,
  workflow = "cws_registration"
} = {}) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [error, setError] = useState("");
  const [displayPrompt, setDisplayPrompt] = useState("");
  const [assistantTranscript, setAssistantTranscript] = useState("");
  const [sessionInfo, setSessionInfo] = useState(null);

  const remoteAudioRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const isConnectingRef = useRef(false);
  const isManualStopRef = useRef(false);
  const transcriptBufferRef = useRef("");
  const sessionDisplayPromptRef = useRef("");

  const cleanupConnection = useCallback(({ resetUiState = false } = {}) => {
    isManualStopRef.current = true;

    const dc = dataChannelRef.current;
    if (dc) {
      dc.onopen = null;
      dc.onmessage = null;
      dc.onerror = null;
      dc.onclose = null;
      try {
        dc.close();
      } catch (_) {}
    }
    dataChannelRef.current = null;

    const pc = peerConnectionRef.current;
    if (pc) {
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      try {
        pc.close();
      } catch (_) {}
    }
    peerConnectionRef.current = null;

    const micStream = localStreamRef.current;
    if (micStream) {
      try {
        micStream.getTracks().forEach((t) => t.stop());
      } catch (_) {}
    }
    localStreamRef.current = null;

    if (remoteAudioRef.current) {
      try {
        remoteAudioRef.current.srcObject = null;
      } catch (_) {}
    }

    isConnectingRef.current = false;

    if (resetUiState) {
      setStatus(STATUS.IDLE);
      setIsEnabled(false);
      setError("");
      setDisplayPrompt("");
      setAssistantTranscript("");
      setSessionInfo(null);
      transcriptBufferRef.current = "";
      sessionDisplayPromptRef.current = "";
    }

    setTimeout(() => {
      isManualStopRef.current = false;
    }, 0);
  }, []);

  const disableAssistant = useCallback(() => {
    cleanupConnection({ resetUiState: true });
  }, [cleanupConnection]);

  const sendEvent = useCallback((eventPayload) => {
    const dc = dataChannelRef.current;
    if (!dc || dc.readyState !== "open") return false;
    try {
      dc.send(JSON.stringify(eventPayload));
      return true;
    } catch (err) {
      console.error("Failed to send realtime event:", err);
      return false;
    }
  }, []);

  const askAssistant = useCallback(
    (userText) => {
      if (!userText) return false;
      const ok = sendEvent({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ type: "input_text", text: userText }]
        }
      });
      if (!ok) return false;

      sendEvent({
        type: "response.create",
        response: { output_modalities: ["audio", "text"] }
      });
      return true;
    },
    [sendEvent]
  );

  const sendStepContext = useCallback(
    ({ stepKey, stepTitle, action, href }) => {
      if (status !== STATUS.ACTIVE) return false;

      const label = stepTitle || stepKey || "next step";
      const actionText =
        action === "open_link"
          ? `I opened the "${label}" next step in a new tab.`
          : `I selected the "${label}" next step instructions.`;

      const linkNote =
        action === "open_link" && href
          ? ` The page opened at ${href}.`
          : "";

      const guidance =
        "Please guide me through what to do next, then tell me what to come back and confirm in this app.";

      return askAssistant(`${actionText}${linkNote} ${guidance}`);
    },
    [askAssistant, status]
  );

  const enableAssistant = useCallback(async () => {
    if (isConnectingRef.current || isEnabled || status === STATUS.CONNECTING) return;
    if (!navigator?.mediaDevices?.getUserMedia || !window?.RTCPeerConnection) {
      setStatus(STATUS.ERROR);
      setError("Your browser does not support realtime voice assistant features.");
      return;
    }

    isConnectingRef.current = true;
    setIsEnabled(true);
    setStatus(STATUS.CONNECTING);
    setError("");
    setAssistantTranscript("");
    transcriptBufferRef.current = "";
    isManualStopRef.current = false;

    try {
      const sessionResp = await fetch(CREATE_REALTIME_SESSION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workflow })
      });

      if (!sessionResp.ok) {
        throw new Error(await normalizeBackendError(sessionResp));
      }

      const sessionPayload = await sessionResp.json();
      const clientSecret = sessionPayload?.client_secret?.value;
      const session = sessionPayload?.session || {};
      const model = session?.model || "gpt-realtime";
      const prompt = String(session?.display_prompt || "").trim();

      if (!clientSecret) {
        throw new Error("Backend did not return client_secret.value.");
      }

      setDisplayPrompt(prompt);
      sessionDisplayPromptRef.current = prompt;
      setSessionInfo(session);

      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = localStream;

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      pc.ontrack = (event) => {
        const audioEl = remoteAudioRef.current;
        if (!audioEl) return;
        audioEl.autoplay = true;
        audioEl.playsInline = true;
        audioEl.srcObject = event.streams[0];
        audioEl.play().catch(() => {});
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        if (isManualStopRef.current) return;
        if (state === "connected") {
          setStatus(STATUS.ACTIVE);
          setError("");
          return;
        }
        if (state === "failed" || state === "disconnected" || state === "closed") {
          cleanupConnection({ resetUiState: false });
          setStatus(STATUS.ERROR);
          setIsEnabled(false);
          setError("Realtime connection failed. Please enable voice assistant again.");
        }
      };

      localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));

      const dc = pc.createDataChannel("oai-events");
      dataChannelRef.current = dc;

      dc.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data || "{}");

          if (payload?.type === "error") {
            const msg =
              payload?.error?.message ||
              "Voice assistant reported an error.";
            setStatus(STATUS.ERROR);
            setError(msg);
            setIsEnabled(false);
            return;
          }

          if (payload?.type === "response.created") {
            transcriptBufferRef.current = "";
            return;
          }

          if (
            payload?.type === "response.output_audio_transcript.delta" &&
            typeof payload?.delta === "string"
          ) {
            transcriptBufferRef.current += payload.delta;
            setAssistantTranscript(transcriptBufferRef.current.trim());
            return;
          }

          if (
            payload?.type === "response.output_audio_transcript.done" &&
            typeof payload?.transcript === "string"
          ) {
            transcriptBufferRef.current = payload.transcript;
            setAssistantTranscript(payload.transcript.trim());
          }
        } catch (_) {}
      };

      const channelOpened = new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          reject(new Error("Realtime data channel did not open in time."));
        }, 15000);

        dc.onopen = () => {
          clearTimeout(timer);
          resolve();
        };

        dc.onerror = () => {
          clearTimeout(timer);
          reject(new Error("Realtime data channel failed to open."));
        };
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResp = await fetch(
        `${OPENAI_REALTIME_CALLS_URL}?model=${encodeURIComponent(model)}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${clientSecret}`,
            "Content-Type": "application/sdp"
          },
          body: offer.sdp
        }
      );

      if (!sdpResp.ok) {
        const errText = await sdpResp.text();
        throw new Error(
          `Realtime connection failed (${sdpResp.status})${errText ? `: ${errText}` : ""}`
        );
      }

      const answerSdp = await sdpResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      await channelOpened;

      setStatus(STATUS.ACTIVE);

      const greetingText =
        sessionDisplayPromptRef.current ||
        "Please guide me through the next steps.";
      askAssistant(greetingText);
    } catch (err) {
      console.error("Voice assistant init failed:", err);
      cleanupConnection({ resetUiState: false });
      setStatus(STATUS.ERROR);
      setIsEnabled(false);
      if (isMicPermissionDenied(err)) {
        setError("Microphone permission denied. Please allow microphone access and try again.");
      } else {
        setError(err?.message || "Could not start voice assistant.");
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [askAssistant, cleanupConnection, isEnabled, status, workflow]);

  useEffect(() => {
    if (!isModalOpen) {
      disableAssistant();
    }
  }, [disableAssistant, isModalOpen]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupConnection({ resetUiState: false });
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      cleanupConnection({ resetUiState: false });
    };
  }, [cleanupConnection]);

  return {
    isEnabled,
    isConnecting: status === STATUS.CONNECTING,
    isActive: status === STATUS.ACTIVE,
    status,
    error,
    displayPrompt,
    assistantTranscript,
    sessionInfo,
    remoteAudioRef,
    enableAssistant,
    disableAssistant,
    sendStepContext
  };
}
